/**
 * Payment Link API Headers Utility
 *
 * This module provides utility functions for generating secure headers
 * for payment link API requests. It includes timestamp generation for
 * request validation and anti-replay attack protection.
 *
 * Security features:
 * - Timestamp validation (prevents replay attacks)
 * - Standardized header format across all payment link requests
 */

/**
 * Generate standard API headers for payment link requests
 * Includes timestamp for request validation and security
 *
 * @param {string} [token] - Per-payment-link JWT issued by POST /api/payment-link,
 *   scoped to a single reff_no/account_id. Required for every endpoint except the
 *   initial fetch that issues it. Omit only for that initial call.
 * @returns {Object} Headers object with Content-Type, Authorization, and X-Timestamp
 */
export const getPaymentLinkHeaders = (token) => {
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    'X-Timestamp': Date.now().toString(),
  };
};

/**
 * Get current timestamp in milliseconds
 *
 * This function returns the current timestamp which is used by the backend
 * to validate that requests are not too old (anti-replay attack protection)
 *
 * @returns {number} Current timestamp in milliseconds
 */
export const getCurrentTimestamp = () => {
  return Date.now();
};

/**
 * Check if a timestamp is still valid (within acceptable time window)
 *
 * @param {number} timestamp - The timestamp to validate (in milliseconds)
 * @param {number} maxAgeSeconds - Maximum age in seconds (default: 300 = 5 minutes)
 * @returns {boolean} True if timestamp is valid, false otherwise
 */
export const isTimestampValid = (timestamp, maxAgeSeconds = 300) => {
  const currentTime = Date.now();
  const timeDifference = Math.abs(currentTime - timestamp);
  return timeDifference <= (maxAgeSeconds * 1000);
};
